import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

function HelpIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="32" height="32" rx="16" fill="#1C7AEB" />
      <path
        d="M8.5 15.1667H11C11.442 15.1667 11.866 15.3423 12.1785 15.6548C12.4911 15.9674 12.6667 16.3913 12.6667 16.8334V19.3334C12.6667 19.7754 12.4911 20.1993 12.1785 20.5119C11.866 20.8244 11.442 21 11 21H10.1667C9.72464 21 9.30072 20.8244 8.98816 20.5119C8.67559 20.1993 8.5 19.7754 8.5 19.3334V15.1667ZM8.5 15.1667C8.5 14.1818 8.69399 13.2065 9.0709 12.2966C9.44781 11.3866 10.0003 10.5598 10.6967 9.86339C11.3931 9.16695 12.2199 8.6145 13.1299 8.23759C14.0398 7.86068 15.0151 7.66669 16 7.66669C16.9849 7.66669 17.9602 7.86068 18.8701 8.23759C19.7801 8.6145 20.6069 9.16695 21.3033 9.86339C21.9997 10.5598 22.5522 11.3866 22.9291 12.2966C23.306 13.2065 23.5 14.1818 23.5 15.1667M23.5 15.1667V19.3334C23.5 19.7754 23.3244 20.1993 23.0118 20.5119C22.6993 20.8244 22.2754 21 21.8333 21H21C20.558 21 20.134 20.8244 19.8215 20.5119C19.5089 20.1993 19.3333 19.7754 19.3333 19.3334V16.8334C19.3333 16.3913 19.5089 15.9674 19.8215 15.6548C20.134 15.3423 20.558 15.1667 21 15.1667H23.5Z"
        stroke="white"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.5 19.3333V21C23.5 21.884 23.1488 22.7319 22.5237 23.357C21.8986 23.9821 21.0507 24.3333 20.1667 24.3333H16"
        stroke="white"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18"
        stroke="#707173"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 6L18 18"
        stroke="#707173"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type Props = {
  // A chat page keeps its Send button in the same corner as this launcher.
  overComposer?: boolean
}

export const ChatlioButton: React.FC<Props> = ({ overComposer = false }) => {
  const widgetBtnRef = useRef<HTMLButtonElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [isOnline, setIsOnline] = useState(false)
  const { t } = useTranslation()

  // Over a chat composer the launcher covered the Send button, so lift it
  // clear and drop the label, which would otherwise sit on the newest
  // messages. Left where it was while Chatlio is open, where it is the close
  // button for Chatlio's panel.
  const lifted = overComposer && !expanded
  const title = t('chatlio_title', 'Need help? Chat with our team')

  const handleClick = useCallback(() => {
    if (typeof window !== 'undefined' && window._chatlio && window._chatlio.showOrHide) {
      window._chatlio.showOrHide()
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && window._chatlio && window._chatlio.isOnline) {
      setIsOnline(window._chatlio.isOnline())
    }

    document.addEventListener('chatlio.visibilityChange', function (e: any) {
      setExpanded(e.data.visibility === 'open' && e.data.expanded)
    })

    document.addEventListener('chatlio.offline', function () {
      setIsOnline(false)
    })

    document.addEventListener('chatlio.online', function () {
      setIsOnline(true)
    })
  }, [])

  return (
    <button
      id="chatlio-widget-button"
      ref={widgetBtnRef}
      className={`fixed ${
        lifted ? 'bottom-24 px-3' : 'bottom-6 px-4'
      } right-6 bg-[#DAE9FC] hover:bg-[#C5DDFA] transition-colors duration-200 rounded-full py-3 shadow-lg w-max flex items-center gap-3 z-50`}
      onClick={handleClick}
      aria-label={lifted ? title : undefined}
      data-chatlio-widget-button
    >
      {expanded ? (
        <CloseIcon />
      ) : (
        <>
          <div className="relative">
            {isOnline && (
              <span className="rounded-full bg-[#38A383] absolute top-0 right-0 w-2.5 h-2.5 border-2 border-white"></span>
            )}
            <HelpIcon />
          </div>
          {!lifted && (
            <span className="text-gray-700 font-medium text-sm">{title}</span>
          )}
        </>
      )}
    </button>
  )
}
